import { supabaseClient } from '../config/supabase';

function firstString(...values) {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
}

function checklistValue(session = {}, key = '') {
    const entry = session?.capture_checklist?.[key];
    if (typeof entry === 'string') return entry.trim();
    if (entry && typeof entry === 'object' && typeof entry.value === 'string') return entry.value.trim();
    return '';
}

function firstName(value = '') {
    return firstString(value).split(/\s+/)[0] || '';
}

function withName(contact = null, fallbackName = '') {
    // A brand-new recipient's placeholder ("New contact (...)") is not a name to put in a template.
    const resolved = firstString(contact?.isNewRecipient ? '' : contact?.name, fallbackName);
    return {
        ...(contact || {}),
        name: resolved,
        first_name: firstName(resolved),
        full_name: resolved,
    };
}

async function maybeSingle(queryPromise) {
    const { data, error } = await queryPromise;
    if (error) return null;
    return data || null;
}

export function buildBaseTemplateContext({ session = null, contact = null, contactName = '' } = {}) {
    const fallbackName = firstString(contactName, checklistValue(session, 'full_name'));
    const resolvedContact = withName(contact, fallbackName);
    return {
        session,
        contact: resolvedContact,
        contactName: resolvedContact.full_name || '',
        trip: session || null,
        engagement: null,
        flight: null,
        assignment: null,
        driver: null,
        vehicle: null,
        timezone: 'America/New_York',
    };
}

export async function loadTemplateContext({
    session = null,
    contact = null,
    contactName = '',
    client = supabaseClient,
} = {}) {
    const base = buildBaseTemplateContext({ session, contact, contactName });
    const orgId = session?.org_id;
    const confirmationNumber = firstString(session?.confirmation_number, checklistValue(session, 'confirmation_number'));
    if (!orgId || !confirmationNumber || !client?.from) return base;

    const engagement = await maybeSingle(
        client
            .from('engagements')
            .select('id, org_id, confirmation_number, pickup_address, dropoff_address, scheduled_at')
            .eq('org_id', orgId)
            .eq('confirmation_number', confirmationNumber)
            .maybeSingle()
    );
    if (!engagement?.id) {
        return {
            ...base,
            trip: {
                ...(base.trip || {}),
                confirmation_number: confirmationNumber,
            },
        };
    }

    const [flight, assignment] = await Promise.all([
        maybeSingle(
            client
                .from('dispatch_engagement_details')
                .select('engagement_id, org_id, flight_number, flight_airline, flight_scheduled_time')
                .eq('org_id', orgId)
                .eq('engagement_id', engagement.id)
                .maybeSingle()
        ),
        maybeSingle(
            client
                .from('current_assignment')
                .select('engagement_id, confirmation_number, driver_id, vehicle_id')
                .eq('engagement_id', engagement.id)
                .eq('confirmation_number', confirmationNumber)
                .maybeSingle()
        ),
    ]);

    const driver = assignment?.driver_id
        ? await maybeSingle(
            client
                .from('drivers')
                .select('id, org_id, user_profile_id, default_vehicle_id')
                .eq('org_id', orgId)
                .eq('id', assignment.driver_id)
                .maybeSingle()
        )
        : null;

    const profile = driver?.user_profile_id
        ? await maybeSingle(
            client
                .from('user_profiles')
                .select('id, full_name, phone')
                .eq('id', driver.user_profile_id)
                .maybeSingle()
        )
        : null;

    const vehicleId = assignment?.vehicle_id || driver?.default_vehicle_id || null;
    const vehicle = vehicleId
        ? await maybeSingle(
            client
                .from('vehicles')
                .select('id, org_id, plate, vehicle_class')
                .eq('org_id', orgId)
                .eq('id', vehicleId)
                .maybeSingle()
        )
        : null;

    const resolvedDriver = driver
        ? {
            ...driver,
            full_name: firstString(profile?.full_name),
            phone: firstString(profile?.phone),
        }
        : null;

    return {
        ...base,
        engagement,
        flight: flight || null,
        assignment: assignment || null,
        driver: resolvedDriver,
        vehicle: vehicle || null,
        trip: {
            ...(base.trip || {}),
            confirmation_number: engagement.confirmation_number || confirmationNumber,
            pickup_address: firstString(engagement.pickup_address),
            dropoff_address: firstString(engagement.dropoff_address),
            scheduled_at: firstString(engagement.scheduled_at),
            flight_number: firstString(flight?.flight_number),
            flight_airline: firstString(flight?.flight_airline),
            flight_scheduled_time: firstString(flight?.flight_scheduled_time),
            pickup_time: firstString(engagement.scheduled_at),
            pickup_date: firstString(engagement.scheduled_at),
            pickup_location: firstString(engagement.pickup_address),
            dropoff_location: firstString(engagement.dropoff_address),
        },
    };
}
